import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import axios from 'axios';

// Pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import OnboardingPage from './pages/OnboardingPage';
import MarketplacePage from './pages/MarketplacePage';
import DealDetailPage from './pages/DealDetailPage';
import PostDealPage from './pages/PostDealPage';
import DirectoryPage from './pages/DirectoryPage';
import ProviderProfilePage from './pages/ProviderProfilePage';
import DashboardPage from './pages/DashboardPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import ForInvestorsPage from './pages/ForInvestorsPage';
import ForAgentsPage from './pages/ForAgentsPage';
import ForProvidersPage from './pages/ForProvidersPage';
import ForWholesalersPage from './pages/ForWholesalersPage';
import AuthCallback from './components/AuthCallback';

// Cleveland Market Pages
import ClevelandInvestorsPage from './pages/markets/ClevelandInvestorsPage';
import ClevelandWholesalersPage from './pages/markets/ClevelandWholesalersPage';
import ClevelandAgentsPage from './pages/markets/ClevelandAgentsPage';
import ClevelandProvidersPage from './pages/markets/ClevelandProvidersPage';
import { SupportButton } from './components/SupportButton';
import ScrollToTop from './components/ScrollToTop';

import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const storedToken = localStorage.getItem('token');
    if (!storedToken) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${storedToken}` }
      });
      setUser(response.data);
      setToken(storedToken);
    } catch (error) {
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = async () => {
    try {
      if (token) {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (e) {
      // Ignore logout errors
    }
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, updateUser, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id (OAuth callback)
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/for-investors" element={<ForInvestorsPage />} />
      <Route path="/for-agents" element={<ForAgentsPage />} />
      <Route path="/for-providers" element={<ForProvidersPage />} />
      <Route path="/for-wholesalers" element={<ForWholesalersPage />} />
      
      {/* Cleveland Market Pages */}
      <Route path="/markets/cleveland/investors" element={<ClevelandInvestorsPage />} />
      <Route path="/markets/cleveland/wholesalers" element={<ClevelandWholesalersPage />} />
      <Route path="/markets/cleveland/agents" element={<ClevelandAgentsPage />} />
      <Route path="/markets/cleveland/providers" element={<ClevelandProvidersPage />} />
      
      <Route path="/marketplace" element={<MarketplacePage />} />
      <Route path="/deals/:dealId" element={<DealDetailPage />} />
      <Route path="/directory" element={<DirectoryPage />} />
      <Route path="/providers/:providerId" element={<ProviderProfilePage />} />
      
      {/* Protected Routes */}
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <OnboardingPage />
        </ProtectedRoute>
      } />
      <Route path="/post-deal" element={
        <ProtectedRoute>
          <PostDealPage />
        </ProtectedRoute>
      } />
      <Route path="/edit-deal/:dealId" element={
        <ProtectedRoute>
          <PostDealPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute adminOnly>
          <AdminDashboardPage />
        </ProtectedRoute>
      } />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AuthProvider>
        <Toaster position="bottom-right" richColors />
        <AppRouter />
        <SupportButton variant="floating" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
